import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SEO_BASE_URL } from '../constants/seo';
import { useAppSelector } from '../store/hooks';
import PublicHeader from '../components/layout/PublicHeader';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';

const GDPRPage: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAppSelector((state) => state.user);
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, []);

    const goToDashboard = () => {
        navigate('/dashboard');
    };

    return (
        <>
            <Helmet>
                <html lang="en" />
                <title>GDPR Compliance - Focuzon | Data Protection</title>
                <meta name="description" content="Learn about Focuzon's commitment to GDPR compliance and how we protect your personal data." />
                <meta name="keywords" content="GDPR, data protection, privacy, Focuzon" />
                <link rel="canonical" href={`${SEO_BASE_URL}/gdpr`} />
                <meta property="og:title" content="GDPR Compliance - Focuzon | Data Protection" />
                <meta property="og:description" content="Learn about Focuzon's commitment to GDPR compliance and how we protect your personal data." />
                <meta property="og:url" content={`${SEO_BASE_URL}/gdpr`} />
                <meta property="og:type" content="website" />
                <meta property="og:image" content={`${SEO_BASE_URL}/og-image.jpg`} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="GDPR Compliance - Focuzon | Data Protection" />
                <meta name="twitter:description" content="Learn about Focuzon's commitment to GDPR compliance and how we protect your personal data." />
            </Helmet>
            <div className="min-h-screen bg-[#FFF8F0] relative overflow-hidden">
                {/* Background gradient shapes */}
                <div className="fixed inset-0 pointer-events-none z-0">
                    <div className="absolute top-0 -left-1/4 w-96 h-96 bg-gradient-to-br from-orange-300/50 to-orange-200/40 rounded-full blur-3xl"></div>
                    <div className="absolute top-1/4 -right-1/4 w-96 h-96 bg-gradient-to-br from-orange-400/50 to-orange-300/40 rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-1/4 w-80 h-80 bg-gradient-to-br from-orange-200/40 to-orange-100/30 rounded-full blur-3xl"></div>
                    <div className="absolute top-1/2 right-1/3 w-72 h-72 bg-gradient-to-br from-orange-300/40 to-orange-200/30 rounded-full blur-3xl"></div>
                </div>

                <PublicHeader />

                {/* Content - Add padding-top for fixed header */}
                <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12 relative z-10">
                    <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-orange-200/50 shadow-lg">
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                            GDPR Compliance
                        </h1>
                        <p className="text-gray-600 mb-8">Last updated: March 1, 2026</p>

                        <div className="space-y-8 text-gray-700 leading-relaxed">
                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
                                <div className="space-y-4">
                                    <p>At Focuzon, we are committed to protecting your personal data and respecting your privacy in accordance with the General Data Protection Regulation (GDPR). This page outlines your rights under GDPR and explains how we handle your data.</p>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Your Rights Under GDPR</h2>
                                <div className="space-y-4">
                                    <p>If you are a resident of the European Economic Area (EEA), you have certain data protection rights. Focuzon aims to take reasonable steps to allow you to correct, amend, delete, or limit the use of your Personal Data.</p>
                                    <ul className="list-disc list-inside space-y-2 ml-4">
                                        <li><strong className="text-gray-900">Right to Access:</strong> You have the right to request copies of your personal data.</li>
                                        <li><strong className="text-gray-900">Right to Rectification:</strong> You have the right to request that we correct any information you believe is inaccurate.</li>
                                        <li><strong className="text-gray-900">Right to Erasure:</strong> You have the right to request that we erase your personal data, under certain conditions.</li>
                                        <li><strong className="text-gray-900">Right to Restrict Processing:</strong> You have the right to request that we restrict the processing of your personal data.</li>
                                        <li><strong className="text-gray-900">Right to Object to Processing:</strong> You have the right to object to our processing of your personal data.</li>
                                        <li><strong className="text-gray-900">Right to Data Portability:</strong> You have the right to request that we transfer the data that we have collected to another organization, or directly to you.</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Data Controller and Data Processor</h2>
                                <div className="space-y-4">
                                    <p>
                                        Focuzon acts as a Data Controller for personal information such as account details and usage data.
                                        In certain cases, we may act as a Data Processor for content and information you choose to store within the application.
                                    </p>

                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Legal Basis for Processing</h2>
                                <div className="space-y-4">
                                    <p>We process your personal data based on the following legal grounds:</p>
                                    <ul className="list-disc list-inside space-y-2 ml-4">
                                        <li><strong className="text-gray-900">Contractual Necessity:</strong> Processing is necessary for the performance of our contract with you (e.g., providing our services).</li>
                                        <li><strong className="text-gray-900">Legitimate Interests:</strong> Processing is necessary for our legitimate interests, such as improving our services and ensuring security.</li>
                                        <li>
                                            <strong className="text-gray-900">Consent:</strong>{' '}
                                            Where required, we process personal data based on your explicit consent.
                                        </li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Transfers Outside the EEA</h2>
                                <div className="space-y-4">
                                    <p>If we transfer your personal data outside the EEA, we ensure a similar degree of protection is afforded to it by ensuring at least one of the following safeguards is implemented:</p>
                                    <ul className="list-disc list-inside space-y-2 ml-4">
                                        <li>We will only transfer your personal data to countries that have been deemed to provide an adequate level of protection for personal data by the European Commission.</li>
                                        <li>Where we use certain service providers, we may use specific contracts approved by the European Commission which give personal data the same protection it has in Europe.</li>
                                    </ul>
                                </div>
                            </section>

                            <section>
                                <h2 className="text-2xl font-semibold text-gray-900 mb-4">
                                    Contact for Data Protection Matters
                                </h2>

                                <div className="space-y-4">
                                    <p>
                                        If you have any questions about data protection, privacy, or GDPR-related requests,
                                        you may contact us using the details below:
                                    </p>

                                    <ul className="list-disc list-inside space-y-2 ml-4">
                                        <li>Email: support@focuzon.com</li>
                                    </ul>
                                </div>
                            </section>
                        </div>

                        <div className="mt-12 pt-8 border-t border-orange-200/50">
                            <div className="text-center">
                                <p className="text-gray-600 text-sm">
                                    For more details, please review our <button onClick={() => setShowPrivacyModal(true)} className="text-orange-600 hover:underline bg-transparent border-0 p-0 cursor-pointer">Privacy Policy</button>.
                                </p>
                            </div>
                        </div>
                    </div>
                </main>
            </div>

            <PrivacyPolicyModal
                isOpen={showPrivacyModal}
                onClose={() => setShowPrivacyModal(false)}
            />
        </>
    );
};

export default GDPRPage;
